import React from 'react';

// adding polyfills
import 'core-js/stable';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from 'react-router-dom';
import 'regenerator-runtime/runtime';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { config } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';

import configureStore from './redux/store';
import { withComponentSeasons } from './views/enhancers';
import * as layouts from './views/layouts';

import './styles/common/app.scss';

const store = configureStore();

const basename = `/${window.recordsSettings.franchiseTricode === 'nhl' ? '' : window.recordsSettings.franchiseTricode}`;

declare global {
    interface Window {
        Rollbar: any;
        rollbarConfig: any;
        emotionCache: any;
        nonce: string;
    }
}

// Get nonce from the root javascript bundle that is generated when we build our client code
const rootBundle = document.querySelector('#root-bundle');
const nonce = (rootBundle as HTMLElement)?.nonce;

// Do not alter this line! I know your IDE is complaining. This will be used by styled-components to set a nonce
// attribute on injected CSS. It has to be set 'globally' but doing window.__webpack_nonce__ doesn't work for some reason.
// @ts-expect-error
__webpack_nonce__ = nonce;

// Set the nonce for emotion based style injection (namely coming from material-ui, and react-select).
const emotionCache = createCache({
    key: 'grb-ui',
    nonce: nonce,
});
window.emotionCache = emotionCache;

// Make sure this is before any other `fontawesome` API calls
config.autoAddCss = false;

const initializeApp = () => {
    const EnhancedApp = withComponentSeasons(layouts.App);
    const rootElement = document.getElementById('root');
    const root = createRoot(rootElement);

    const basicRoutes = [
        <Route
            key="no-match"
            path="*"
            lazy={() => import(/* webpackChunkName: "NoMatch" */ './views/pages/noMatch')}
        />,
        <Route key="home" path="/" lazy={() => import(/* webpackChunkName: "Home" */ './views/pages/home')} />,

        // Coaches
        <Route
            key="head-coach"
            path="/coaches/:id?"
            lazy={() => import(/* webpackChunkName: "HeadCoach" */ './views/pages/headCoach')}
        />,
        <Route
            key="regular-season-coach-registry"
            path="/registry/head-coach-regular-season"
            lazy={() =>
                import(
                    /* webpackChunkName: "RegularSeasonCoachRegistry" */ './views/pages/coachRecords/RegularSeasonCoachRegistry'
                )
            }
        />,
        <Route
            key="playoff-coach-registry"
            path="/registry/head-coach-playoffs"
            lazy={() =>
                import(/* webpackChunkName: "PlayoffCoachRegistry" */ './views/pages/coachRecords/PlayoffCoachRegistry')
            }
        />,
        <Route
            key="regular-season-coach-records"
            path="/records/coach-records/*"
            lazy={() =>
                import(
                    /* webpackChunkName: "RegularSeasonCoachRecords" */ './views/pages/coachRecords/RegularSeasonCoachRecords'
                )
            }
        />,
        <Route
            key="playoff-coach-records"
            path="/records/playoff-coach-records/*"
            lazy={() =>
                import(/* webpackChunkName: "PlayoffCoachRecords" */ './views/pages/coachRecords/PlayoffCoachRecords')
            }
        />,

        // General Manager
        <Route
            key="general-manager"
            path="/general-managers/:id?"
            lazy={() => import(/* webpackChunkName: "GeneralManager" */ './views/pages/generalManager')}
        />,
        <Route
            key="regular-season-general-manager-registry"
            path="/records/general-manager-registry/*"
            lazy={() =>
                import(
                    /* webpackChunkName: "RegularSeasonGeneralManagerRegistry" */ './views/pages/generalManagerRegistry/RegularSeasonGeneralManagerRegistry'
                )
            }
        />,
        <Route
            key="playoff-general-manager-registry"
            path="/records/playoff-general-manager-registry/*"
            lazy={() =>
                import(
                    /* webpackChunkName: "PlayoffGeneralManagerRegistry" */ './views/pages/generalManagerRegistry/PlayoffGeneralManagerRegistry'
                )
            }
        />,
        <Route
            key="general-manager-records"
            path="/records/general-manager-records/*"
            lazy={() => import(/* webpackChunkName: "GeneralManagerRecords" */ './views/pages/generalManagerRecords')}
        />,
        <Route
            key="playoff-general-manager-records"
            path="/records/playoff-general-manager-records/*"
            lazy={() =>
                import(
                    /* webpackChunkName: "PlayoffGeneralManagerRecords" */ './views/pages/playoffGeneralManagerRecords'
                )
            }
        />,

        // Regular Season records
        <Route
            key="skater-records"
            path="/records/skater-records/*"
            lazy={() => import(/* webpackChunkName: "SkaterRecords" */ './views/pages/skaterRecords')}
        />,
        <Route
            key="goaltender-records"
            path="/records/goaltender-records/*"
            lazy={() => import(/* webpackChunkName: "GoaltenderRecords" */ './views/pages/goaltenderRecords')}
        />,
        <Route
            key="team-records"
            path="/records/team-records/*"
            lazy={() => import(/* webpackChunkName: "TeamRecords" */ './views/pages/teamRecords')}
        />,
        <Route
            key="penalty-shots"
            path="/records/penalty-shots"
            lazy={() =>
                import(
                    /* webpackChunkName: "RegularSeasonPenaltyShots" */ './views/pages/penaltyShots/RegularSeasonPenaltyShots'
                )
            }
        />,
        <Route
            key="milestones"
            path="/records/milestones/:view"
            lazy={() => import(/* webpackChunkName: "Milestones" */ './views/pages/milestones')}
        />,
        <Route
            key="individual-scoring-records"
            path="/records/individual-scoring-records/:view"
            lazy={() =>
                import(/* webpackChunkName: "IndividualScoringRecords" */ './views/pages/individualScoringRecords')
            }
        />,

        // Playoff Records
        <Route
            key="playoff-skater-records"
            path="/records/playoff-skater-records/*"
            lazy={() => import(/* webpackChunkName: "PlayoffSkaterRecords" */ './views/pages/playoffSkaterRecords')}
        />,
        <Route
            key="playoff-goaltender-records"
            path="/records/playoff-goaltender-records/*"
            lazy={() =>
                import(/* webpackChunkName: "PlayoffGoaltenderRecords" */ './views/pages/playoffGoaltenderRecords')
            }
        />,
        <Route
            key="playoff-team-records"
            path="/records/playoff-team-records/*"
            lazy={() => import(/* webpackChunkName: "PlayoffTeamRecords" */ './views/pages/playoffTeamRecords')}
        />,
        <Route
            key="playoff-penalty-shots"
            path="/records/playoff-penalty-shots"
            lazy={() =>
                import(/* webpackChunkName: "PlayoffPenaltyShots" */ './views/pages/penaltyShots/PlayoffPenaltyShots')
            }
        />,
        <Route
            key="scf-goaltender-records"
            path="/records/scf-goaltender-records/*"
            lazy={() => import(/* webpackChunkName: "SCFGoalieRecords" */ './views/pages/scfGoalieRecords')}
        />,
        <Route
            key="scf-skater-records"
            path="/records/scf-skater-records/*"
            lazy={() => import(/* webpackChunkName: "SCFSkaterRecords" */ './views/pages/scfSkaterRecords')}
        />,
        <Route
            key="scf-team-records"
            path="/records/scf-team-records/*"
            lazy={() => import(/* webpackChunkName: "SCFTeamRecords" */ './views/pages/scfTeamRecords')}
        />,

        // Outdoor Records
        <Route
            key="outdoor-skater-records"
            path="/records/outdoor-skater-records/*"
            lazy={() =>
                import(/* webpackChunkName: "OutdoorSkaterRecords" */ './views/pages/outdoorRecords/skaterRecords')
            }
        />,
        <Route
            key="outdoor-goaltender-records"
            path="/records/outdoor-goaltender-records/*"
            lazy={() =>
                import(
                    /* webpackChunkName: "OutdoorGoaltenderRecords" */ './views/pages/outdoorRecords/goaltenderRecords'
                )
            }
        />,
        <Route
            key="outdoor-team-records"
            path="/records/outdoor-team-records/*"
            lazy={() => import(/* webpackChunkName: "OutdoorTeamRecords" */ './views/pages/outdoorRecords/teamRecords')}
        />,

        // International Records
        <Route
            key="international-skater-records"
            path="/records/international-skater-records/*"
            lazy={() =>
                import(
                    /* webpackChunkName: "InternationalSkaterRecords" */ '~/views/pages/internationalTournament/records/InternationalSkaterRecords'
                )
            }
        />,
        <Route
            key="international-goaltender-records"
            path="/records/international-goaltender-records/*"
            lazy={() =>
                import(
                    /* webpackChunkName: "InternationalGoaltenderRecords" */ '~/views/pages/internationalTournament/records/InternationalGoaltenderRecords'
                )
            }
        />,
        <Route
            key="international-team-records"
            path="/records/international-team-records/*"
            lazy={() =>
                import(
                    /* webpackChunkName: "InternationalTeamRecords" */ '~/views/pages/internationalTournament/records/InternationalTeamRecords'
                )
            }
        />,

        // Standings
        <Route
            key="all-time-standings"
            path="/standings/all-time-standings"
            lazy={() =>
                import(/* webpackChunkName: "AllTimeStandings" */ './views/pages/standings/components/allTimeStandings')
            }
        />,
        <Route
            key="playoff-standings"
            path="/standings/playoff-standings"
            lazy={() =>
                import(/* webpackChunkName: "PlayoffStandings" */ './views/pages/standings/components/playoffStandings')
            }
        />,
        <Route
            key="season-opener-standings"
            path="/standings/season-opener-standings/:view"
            lazy={() =>
                import(
                    /* webpackChunkName: "SeasonOpenerStandings" */ './views/pages/standings/components/seasonOpenerStandings'
                )
            }
        />,
        <Route
            key="all-time-overtime-standings"
            path="/standings/all-time-regular-season-overtime-standings"
            lazy={() =>
                import(
                    /* webpackChunkName: "AllTimeOvertimeStandings" */ './views/pages/standings/components/allTimeOvertimeStandings'
                )
            }
        />,
        <Route
            key="all-time-playoff-overtime-standings"
            path="/standings/all-time-playoff-overtime-standings"
            lazy={() =>
                import(
                    /* webpackChunkName: "AllTimePlayoffOvertimeStandings" */ './views/pages/standings/components/allTimePlayoffOvertimeStandings'
                )
            }
        />,
        <Route
            key="all-time-game-seven-standings"
            path="/standings/all-time-game-seven-standings"
            lazy={() =>
                import(
                    /* webpackChunkName: "AllTimeGameSevenStandings" */ './views/pages/standings/components/allTimeGameSevenStandings'
                )
            }
        />,
        <Route
            key="all-time-scf-standings"
            path="/standings/all-time-scf-standings"
            lazy={() =>
                import(
                    /* webpackChunkName: "AllTimeSCFStandings" */ './views/pages/standings/components/allTimeScfStandings'
                )
            }
        />,

        // Franchise
        <Route
            key="franchises"
            path="/franchises/:view?"
            lazy={() => import(/* webpackChunkName: "FranchiseLanding" */ './views/pages/franchiseLanding')}
        />,

        // Awards
        <Route
            key="awards-stanley-cup"
            path="/awards/stanley-cup/:view"
            lazy={() => import(/* webpackChunkName: "StanleyCupAwards" */ './views/pages/stanleyCup')}
        />,
        <Route
            key="all-awards"
            path="/awards/trophies"
            lazy={() => import(/* webpackChunkName: "AllAwards" */ './views/pages/awards')}
        />,
        <Route
            key="specific-award"
            path="/awards/trophies/:slug?"
            lazy={() => import(/* webpackChunkName: "SpecificAward" */ './views/pages/award')}
        />,
        <Route
            key="three-stars"
            path="/awards/three-stars"
            lazy={() => import(/* webpackChunkName: "ThreeStars" */ './views/pages/threeStars')}
        />,
        <Route
            key="phwa-all-star-teams"
            path="/awards/phwa-all-star-teams"
            lazy={() => import(/* webpackChunkName: "PHWAAllStarTeams" */ './views/pages/phwaAllStarTeams')}
        />,
        <Route
            key="all-star-team-position-leaders"
            path="/awards/all-star-team-position-leaders"
            lazy={() =>
                import(/* webpackChunkName: "AllStarTeamPositionLeaders" */ './views/pages/allStarTeamPositionLeaders')
            }
        />,

        // Playoff Summary & All Star
        <Route
            key="playoff-summary"
            path="/playoff-summary/:view"
            lazy={() => import(/* webpackChunkName: "PlayoffSummary" */ './views/pages/playoffSummary')}
        />,
        <Route
            key="all-star"
            path="/all-star/*"
            lazy={() => import(/* webpackChunkName: "AllStar" */ './views/pages/allStar')}
        />,

        // Draft
        <Route
            key="combine"
            path="draft/combine/:view"
            lazy={() => import(/* webpackChunkName: "Combine" */ './views/pages/combine')}
        />,
        <Route
            key="draft-hosts"
            path="/draft/draft-hosts"
            lazy={() => import(/* webpackChunkName: "DraftHosts" */ './views/pages/draft/components/draftHosts')}
        />,
        <Route
            key="first-overall"
            path="/draft/no.-1-overall-picks"
            lazy={() => import(/* webpackChunkName: "FirstOverall" */ './views/pages/draft/components/firstOverall')}
        />,
        <Route
            key="draft-analysis"
            path="/draft/draft-analysis"
            lazy={() => import(/* webpackChunkName: "DraftAnalysis" */ './views/pages/draft/components/draftAnalysis')}
        />,
        <Route
            key="draft-lottery"
            path="/draft/draft-lottery"
            lazy={() => import(/* webpackChunkName: "DraftLottery" */ './views/pages/draft/components/draftLottery')}
        />,
        <Route
            key="expansion-draft"
            path="/draft/expansion-draft"
            lazy={() =>
                import(/* webpackChunkName: "ExpansionDraft" */ './views/pages/draft/components/expansionDraft')
            }
        />,
        <Route
            key="draft-picks"
            path="/draft/:view?"
            lazy={() => import(/* webpackChunkName: "DraftPicks" */ './views/pages/draft/components/draftPicks')}
        />,

        // Outdoor Registry
        <Route
            key="outdoor-games-goalie-registry"
            path="/registry/outdoor-games-goalie"
            lazy={() =>
                import(
                    /* webpackChunkName: "OutdoorGamesGoalieRegistry" */ './views/pages/outdoorGamesRegistry/OutdoorGamesGoalieRegistry'
                )
            }
        />,
        <Route
            key="outdoor-games-skater-registry"
            path="/registry/outdoor-games-skater"
            lazy={() =>
                import(
                    /* webpackChunkName: "OutdoorGamesSkaterRegistry" */ './views/pages/outdoorGamesRegistry/OutdoorGamesSkaterRegistry'
                )
            }
        />,

        // Events
        <Route
            key="all-star-records"
            path="/events/all-star-records/:view"
            lazy={() => import(/* webpackChunkName: "AllStarRecords" */ './views/pages/allStarRecords')}
        />,

        // Events - International Tournament Overview
        <Route
            key="international-tournaments-winner"
            path="/events/international-tournament/overview/winner"
            lazy={() =>
                import(
                    /* webpackChunkName: "InternationalTournamentsWinners" */ '~/views/pages/internationalTournament/overview/winners/Winners'
                )
            }
        />,
        <Route
            key="international-tournaments-clinching-goal"
            path="/events/international-tournament/overview/clinching-goal"
            lazy={() =>
                import(
                    /* webpackChunkName: "InternationalTournamentsClinchingGoals" */ '~/views/pages/internationalTournament/overview/clinchingGoals/ClinchingGoals'
                )
            }
        />,
        <Route
            key="international-tournament-history"
            path="/events/international-tournament/overview/history"
            lazy={() =>
                import(
                    /* webpackChunkName: "InternationalTournamentHistory" */ '~/views/pages/internationalTournament/overview/history/History'
                )
            }
        />,
        <Route
            key="international-tournament-did-you-know"
            path="/events/international-tournament/overview/did-you-know"
            lazy={() =>
                import(
                    /* webpackChunkName: "InternationalTournamentDidYouKnow" */ '~/views/pages/internationalTournament/overview/didYouKnow/DidYouKnow'
                )
            }
        />,

        // Events - International Registers
        <Route
            key="international-tournaments-skater-register"
            path="/events/international-tournament/register/skater"
            lazy={() =>
                import(
                    /* webpackChunkName: "InternationalTournamentsSkaterRegister" */ '~/views/pages/internationalTournament/registry/skaterRegistry/InternationalTournamentsSkaterRegister'
                )
            }
        />,
        <Route
            key="international-tournaments-goaltender-register"
            path="/events/international-tournament/register/goaltender"
            lazy={() =>
                import(
                    /* webpackChunkName: "InternationalTournamentsGoaltenderRegister" */ '~/views/pages/internationalTournament/registry/goaltenderRegistry/InternationalTournamentsGoaltenderRegister'
                )
            }
        />,

        // Events - International Tournament Recaps
        <Route
            key="international-tournament-champion"
            path="/events/international-tournament/tournament-recap/tournament-champion"
            lazy={() =>
                import(
                    /* webpackChunkName: "TournamentChampion" */ '~/views/pages/internationalTournament/recap/tournamentChampion/TournamentChampion'
                )
            }
        />,
        <Route
            key="international-tournament-mvp"
            path="/events/international-tournament/tournament-recap/tournament-mvp"
            lazy={() =>
                import(
                    /* webpackChunkName: "TournamentMVP" */ '~/views/pages/internationalTournament/recap/tournamentMVP/TournamentMVP'
                )
            }
        />,
        <Route
            key="international-goaltender-tournament-statistics"
            path="/events/international-tournament/tournament-recap/tournament-goaltender-statistics"
            lazy={() =>
                import(
                    /* webpackChunkName: "GoaltenderTournamentStats" */ '~/views/pages/internationalTournament/recap/goaltenderStats/GoaltenderTournamentStats'
                )
            }
        />,
        <Route
            key="international-skater-tournament-statistics"
            path="/events/international-tournament/tournament-recap/tournament-skater-statistics"
            lazy={() =>
                import(
                    /* webpackChunkName: "SkaterTournamentStats" */ '~/views/pages/internationalTournament/recap/skaterStats/SkaterTournamentStats'
                )
            }
        />,
        <Route
            key="international-tournament-result"
            path="/events/international-tournament/tournament-recap/tournament-result"
            lazy={() =>
                import(
                    /* webpackChunkName: "InternationalTournamentResults" */ '~/views/pages/internationalTournament/recap/tournamentResults/InternationalTournamentsResults'
                )
            }
        />,
        <Route
            key="international-tournament-roster"
            path="/events/international-tournament/tournament-recap/tournament-roster"
            lazy={() =>
                import(
                    /* webpackChunkName: "InternationalRoster" */ '~/views/pages/internationalTournament/recap/roster/Roster'
                )
            }
        />,
        <Route
            key="international-team-tournament-statistics"
            path="/events/international-tournament/tournament-recap/tournament-team-statistics"
            lazy={() =>
                import(
                    /* webpackChunkName: "TeamTournamentStats" */ '~/views/pages/internationalTournament/recap/teamStats/TeamTournamentStats'
                )
            }
        />,

        <Route
            key="outdoor-game-standings"
            path="/events/standings/outdoor-game-standings"
            lazy={() =>
                import(
                    /* webpackChunkName: "OutdoorGameStandings" */ './views/pages/eventGameStandings/components/outdoorGameStandings'
                )
            }
        />,
        <Route
            key="games-outside-north-america-standings"
            path="/events/standings/standings-for-games-played-outside-north-america"
            lazy={() =>
                import(
                    /* webpackChunkName: "GamesOutsideNorthAmericaStandings" */ './views/pages/eventGameStandings/components/gamesOutsideNorthAmericaStandings/'
                )
            }
        />,
        <Route
            key="events"
            path="/events/:view?"
            lazy={() => import(/* webpackChunkName: "Events" */ './views/pages/events')}
        />,

        // History
        <Route
            key="attendance"
            path="/history/attendance"
            lazy={() => import(/* webpackChunkName: "Attendance" */ './views/pages/attendance')}
        />,
        <Route
            key="hall-of-fame-offices"
            path="/history/hall-of-fame"
            lazy={() => import(/* webpackChunkName: "HallOfFameOffices" */ './views/pages/hallOfFameOffices')}
        />,
        <Route
            key="all-time-playoff-formats"
            path="/history/playoff-formats"
            lazy={() => import(/* webpackChunkName: "AllTimePlayoffFormats" */ './views/pages/allTimePlayoffFormats')}
        />,
        <Route
            key="yearly-playoff-results"
            path="/history/yearly-playoff-results"
            lazy={() => import(/* webpackChunkName: "YearlyPlayoffResults" */ './views/pages/yearlyPlayoffResults')}
        />,
        <Route
            key="all-time-game-sevens"
            path="/history/all-time-game-7-history"
            lazy={() => import(/* webpackChunkName: "AllTimeGameSevens" */ './views/pages/allTimeGameSevens')}
        />,
        <Route
            key="historical-rules"
            path="/history/historical-rule-changes"
            lazy={() => import(/* webpackChunkName: "HistoricalRules" */ './views/pages/historicalRules')}
        />,
        <Route
            key="history"
            path="/history"
            lazy={() => import(/* webpackChunkName: "History" */ './views/pages/history')}
        />,

        // Organization
        <Route
            key="board-of-governors"
            path="/organization/board-of-governors"
            lazy={() => import(/* webpackChunkName: "BoardOfGovernors" */ './views/pages/boardOfGovernors')}
        />,
        <Route
            key="nhl-offices"
            path="/organization/nhl-offices"
            lazy={() => import(/* webpackChunkName: "NHLOffices" */ './views/pages/offices')}
        />,
        <Route
            key="nhlpa-offices"
            path="/organization/nhlpa-offices"
            lazy={() => import(/* webpackChunkName: "NHLPAOffices" */ './views/pages/NHLPAOffices')}
        />,
        <Route
            key="officials"
            path="/organization/officials/:view"
            lazy={() => import(/* webpackChunkName: "Officials" */ './views/pages/officials')}
        />,
        <Route
            key="organization"
            path="/organization"
            lazy={() => import(/* webpackChunkName: "Organization" */ './views/pages/organization')}
        />,
    ];

    const franchiseRoutes = [
        <Route
            key="franchise"
            path="/"
            lazy={() => import(/* webpackChunkName: "Franchise" */ './views/pages/franchise')}
        />,
        <Route
            key="franchise-awards"
            path="/awards/:view?"
            lazy={() => import(/* webpackChunkName: "FranchiseAwards" */ './views/franchisePages/awards')}
        />,
        <Route
            key="franchise-overview"
            path="/overview/:view?"
            lazy={() => import(/* webpackChunkName: "FranchiseOverview" */ './views/pages/franchise')}
        />,
        ...basicRoutes,
    ];

    const router = createBrowserRouter(
        createRoutesFromElements(
            <Route path="/" element={<EnhancedApp />}>
                {window.recordsSettings.franchiseTricode === 'nhl' ? basicRoutes : franchiseRoutes}
            </Route>
        ),
        {
            basename,
        }
    );

    root.render(
        <CacheProvider value={emotionCache}>
            <Provider store={store}>
                <HelmetProvider>
                    <RouterProvider router={router} fallbackElement={<LoadingDisplay isLoading />} />
                </HelmetProvider>
            </Provider>
        </CacheProvider>
    );
};

// Initializing rollbar
if (process.env.NODE_ENV === 'production') {
    import('rollbar').then(({ default: Rollbar }) => {
        window.Rollbar = new Rollbar(window.rollbarConfig);
        initializeApp();
    });
} else {
    initializeApp();
}
